import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { type FaixaDoHistograma, type Metricas as Dados, api, explicarErro } from '../lib/api.js';
import {
  BarraDeFiltros,
  BlocoDeAjuda,
  CabecalhoDeSecao,
  SemDadosSuficientes,
  Vazio,
} from './ui.js';

/**
 * Métricas — o `npm run relatorio:sombra` na tela.
 *
 * O item central é o histograma de cosseno por veredito: é o que permite
 * recalibrar `FAQ_THRESHOLD_LOW` e `FAQ_THRESHOLD_HIGH` com dado real. Por
 * isso ele vem primeiro, com as marcas de LOW e HIGH no lugar onde os limites
 * estão hoje — a leitura que interessa é "a precisão desaba a partir de que
 * faixa", e isso só é visível com a marca ao lado da barra.
 */

function pct(fracao: number | null): string {
  return fracao === null ? '—' : `${(fracao * 100).toFixed(1)}%`;
}

const ROTULO_ZONA: Record<FaixaDoHistograma['zona'], string> = {
  abaixo_low: '< LOW',
  entre: '',
  acima_high: '≥ HIGH',
};

export function Metricas(): JSX.Element {
  const [dados, setDados] = useState<Dados | null>(null);
  const [dias, setDias] = useState('7');
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<Dados>(`/admin/metricas?dias=${dias}`));
      setErro(null);
    } catch (e) {
      setErro(explicarErro(e, 'falha ao carregar as métricas'));
    }
  }, [dias]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (!dados) {
    return erro ? (
      <Alert color="red" variant="light" role="alert">
        {erro}
      </Alert>
    ) : (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const { totais, vereditos, histograma } = dados;
  const maiorBarra = Math.max(
    1,
    ...histograma.faixas.map((f) => f.positivos + f.negativos),
  );

  return (
    <Stack gap="sm">
      <BarraDeFiltros>
        <Select
          size="xs"
          label="Período"
          w={180}
          allowDeselect={false}
          value={dias}
          onChange={(v) => setDias(v ?? '7')}
          data={[
            { value: '7', label: 'Últimos 7 dias' },
            { value: '30', label: 'Últimos 30 dias' },
            { value: '90', label: 'Últimos 90 dias' },
            { value: '365', label: 'Último ano' },
          ]}
        />
        <Button size="xs" onClick={() => void carregar()}>
          Recarregar
        </Button>
        <Text size="sm" c="dimmed">
          desde {new Date(dados.desde).toLocaleDateString('pt-BR')} · modo{' '}
          <strong>{dados.modo}</strong>
        </Text>
      </BarraDeFiltros>

      {/*
        Esta tela fala em cosseno, RRF e nome de variável de ambiente. O
        subtítulo do bloco 01 já diz para que o gráfico serve, e o estado sem
        amostra já explica que a amostra vem dos vereditos no grupo de sombra.
        O que falta é a tradução: o que essas palavras são em linguagem de
        operação, e que esta tela NÃO é a do dia a dia.
      */}
      <BlocoDeAjuda
        secoes={[
          {
            id: 'como',
            titulo: 'Para que serve esta tela',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Esta tela é de ajuste, não de operação.</strong> Ninguém precisa abrir
                  ela todo dia. Ela existe para responder uma pergunta de vez em quando:{' '}
                  <em>o bot está confiando demais, ou de menos?</em> O trabalho do dia — quem está
                  esperando, o que falta responder — está em Início, Transbordos e Lacunas.
                </Text>
                <Text size="sm">
                  <strong>“Cosseno” é a nota de parecença.</strong> Quando o aluno pergunta algo,
                  o bot mede o quanto a pergunta dele se parece com cada FAQ da base. A nota vai de
                  0 a 1, e ela decide o caminho: <strong>acima de HIGH</strong> ele responde direto;{' '}
                  <strong>entre LOW e HIGH</strong> ele não confia na nota sozinha e consulta um
                  modelo de linguagem para julgar se aquela FAQ responde mesmo — que pode concluir
                  que nenhuma serve; <strong>abaixo de LOW</strong> ele nem tenta: registra como
                  lacuna e passa para a secretaria. <strong>LOW</strong> e <strong>HIGH</strong> são
                  onde esses dois cortes estão hoje.
                </Text>
                <Text size="sm">
                  <strong>A tabela diz onde os cortes deveriam estar.</strong> Ela agrupa as
                  respostas por faixa de nota e mostra, em cada faixa, quantas a secretaria aprovou
                  (👍) e quantas reprovou (👎). Onde o 👎 começa a aparecer, a nota já não garante
                  acerto — é candidata a LOW. De onde o 👍 é consistente para cima, dá para
                  responder sem perguntar — candidata a HIGH. Mudar os cortes é mexer na
                  configuração do servidor, não aqui.
                </Text>
                <Text size="sm">
                  <strong>Sem reação da secretaria no grupo, não há o que calibrar.</strong> A
                  amostra desta tela são os 👍 e 👎 que alguém põe nos relatórios do grupo de
                  sombra. Reagir aos relatórios não é burocracia: é o único jeito de o bot saber
                  que errou. Faixa com duas ou três respostas não decide nada.
                </Text>
                <Text size="sm">
                  <strong>Sombra e live.</strong> Em <strong>sombra</strong>, o bot processa tudo
                  mas não manda nada ao aluno — manda um relatório ao grupo para alguém julgar. É o
                  modo em que esta tela se enche de amostra, e em que errar não custa nada. Em{' '}
                  <strong>live</strong>, o aluno recebe as respostas e não há mais relatório para
                  julgar: a calibração tem de estar feita antes. O modo atual aparece no alto da
                  tela de Início.
                </Text>
              </>
            ),
          },
        ]}
      />

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}

      <CabecalhoDeSecao
        numero="01"
        titulo="Cosseno do topo do RRF × veredito"
        subtitulo="O gráfico que recalibra FAQ_THRESHOLD_LOW e HIGH com dado real, em vez dos exemplos sintéticos."
      />

      {histograma.amostras === 0 ? (
        <SemDadosSuficientes titulo="Sem amostra no período.">
          Esta tabela precisa de respostas que tenham recebido 👍 ou 👎 da secretaria no grupo de
          sombra — é a reação que diz se a FAQ escolhida estava certa. Sem veredito não há como
          recalibrar, e os limites continuam nos valores de partida: LOW={histograma.limiteBaixo},
          HIGH={histograma.limiteAlto}.
        </SemDadosSuficientes>
      ) : (
        <>
          <Table.ScrollContainer minWidth={700}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Faixa</Table.Th>
                  <Table.Th>👍</Table.Th>
                  <Table.Th>👎</Table.Th>
                  <Table.Th>Precisão</Table.Th>
                  <Table.Th>Distribuição</Table.Th>
                  <Table.Th>Zona</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {histograma.faixas.map((f) => {
                  const total = f.positivos + f.negativos;
                  return (
                    <Table.Tr key={f.de}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {f.de.toFixed(2)}–{f.ate.toFixed(2)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{f.positivos}</Table.Td>
                      {/* 👎 na coluna com cor E cabeçalho próprio: a cor é
                          reforço, o número é o dado. */}
                      <Table.Td>
                        <Text size="sm" c={f.negativos > 0 ? 'red' : undefined} fw={f.negativos > 0 ? 600 : undefined}>
                          {f.negativos}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {pct(f.precisao)}
                        </Text>
                      </Table.Td>
                      <Table.Td w={180}>
                        <Progress.Root size="md" w={`${(total / maiorBarra) * 100}%`} miw={4}>
                          <Tooltip label={`${f.positivos} positivo(s)`} disabled={f.positivos === 0}>
                            <Progress.Section
                              value={total === 0 ? 0 : (f.positivos / total) * 100}
                              color="teal"
                            />
                          </Tooltip>
                          <Tooltip label={`${f.negativos} negativo(s)`} disabled={f.negativos === 0}>
                            <Progress.Section
                              value={total === 0 ? 0 : (f.negativos / total) * 100}
                              color="red"
                            />
                          </Tooltip>
                        </Progress.Root>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {ROTULO_ZONA[f.zona]}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Text size="xs" c="dimmed">
            {histograma.amostras} amostra(s). Limites em vigor:{' '}
            <strong>LOW={histograma.limiteBaixo}</strong> ·{' '}
            <strong>HIGH={histograma.limiteAlto}</strong>. Para recalibrar: procure a faixa em que
            a precisão desaba (candidata a LOW) e a faixa a partir da qual ela é consistentemente
            alta (candidata a HIGH). Faixa com 2 ou 3 amostras não decide nada.
          </Text>
        </>
      )}

      <CabecalhoDeSecao
        numero="02"
        titulo="Mensagens e vereditos"
        subtitulo="O volume do período e quanto dele foi julgado pela secretaria."
      />
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
        <Cartao rotulo="Rodadas registradas" valor={totais.rodadas} />
        <Cartao rotulo="Respondidas com FAQ" valor={totais.respondidas} />
        <Cartao rotulo="Lacunas" valor={totais.lacunas} />
        <Cartao rotulo="Bloqueio de pagamento" valor={totais.bloqueiosPagamento} />
        <Cartao rotulo="Atendimentos" valor={totais.atendimentos} />
        <Cartao
          rotulo="Precisão aparente"
          valor={pct(vereditos.precisao)}
          nota={
            vereditos.precisao === null
              ? 'nenhum veredito contável'
              : `${vereditos.positivos}/${vereditos.positivos + vereditos.negativos}`
          }
        />
      </SimpleGrid>
      <Text size="xs" c="dimmed">
        👍 {vereditos.positivos} · 👎 {vereditos.negativos} · ❓ {vereditos.indefinidos} (emoji
        fora da lista) · ↩️ {vereditos.removidos} (reação desfeita) · ⬜ {vereditos.semReacao} sem
        reação. Indefinido e removido <strong>não entram</strong> na precisão: não são julgamento
        sobre a resposta.
      </Text>

      <CabecalhoDeSecao
        numero="03"
        titulo="Desfechos"
        subtitulo="Em que cada atendimento terminou. A base de comparação das outras taxas."
      />
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Desfecho</Table.Th>
            <Table.Th>Atendimentos</Table.Th>
            <Table.Th>Fração</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {dados.desfechos.map((d) => (
            <Table.Tr key={d.desfecho}>
              <Table.Td>{d.desfecho}</Table.Td>
              <Table.Td>{d.total}</Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {pct(d.fracao)}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {dados.desfechos.length === 0 && (
        <Text size="sm" c="dimmed">
          Nenhum atendimento no período.
        </Text>
      )}
      <Text size="xs" c="dimmed">
        Taxa de transbordo geral: <strong>{pct(dados.taxaTransbordoGeral)}</strong>. “Abandono” só
        é confiável depois de o varredor rodar.
      </Text>

      <CabecalhoDeSecao
        numero="04"
        titulo="Transbordo por nó"
        subtitulo="Onde o bot desiste, com a taxa sobre quem passou pelo nó — não só o volume."
      />
      {dados.transbordoPorNo.length === 0 ? (
        <Vazio titulo="Nenhum transbordo no período.">
          Quando o bot passar uma conversa adiante, o nó em que ele desistiu aparece aqui com a
          taxa — e taxa alta num nó pouco usado é um problema diferente de volume alto num nó
          movimentado.
        </Vazio>
      ) : (
        <Table.ScrollContainer minWidth={620}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nó</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Transbordos</Table.Th>
                <Table.Th>Passaram pelo nó</Table.Th>
                <Table.Th>Taxa</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dados.transbordoPorNo.map((t) => (
                <Table.Tr key={t.no}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {t.no}
                    </Text>
                  </Table.Td>
                  <Table.Td>{t.categoria ?? '—'}</Table.Td>
                  <Table.Td>{t.transbordos}</Table.Td>
                  <Table.Td>{t.atendimentosNoNo}</Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {pct(t.taxa)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Text size="xs" c="dimmed">
        A “categoria” aqui é <em>onde o aluno estava</em> quando o bot desistiu, não uma
        classificação da pergunta — é o que o registro sabe. A taxa tem denominador próprio
        (atendimentos que passaram pelo nó): volume alto num nó movimentado não é o mesmo problema
        que taxa alta num nó pouco usado.
      </Text>

      {dados.transbordoPorMotivo.length > 0 && (
        <Text size="xs" c="dimmed">
          Por motivo:{' '}
          {dados.transbordoPorMotivo
            .map((t) => `${t.motivo} ${t.total} (${pct(t.fracao)})`)
            .join(' · ')}
        </Text>
      )}

      <CabecalhoDeSecao
        numero="05"
        titulo="FAQs acionadas"
        subtitulo="Ordenado por 👎 primeiro: quem abre esta tela quer ver o que está errado, não o ranking de uso."
      />
      {dados.faqs.length === 0 ? (
        <Vazio titulo="Nenhuma FAQ acionada no período.">
          Nenhuma pergunta chegou à busca nesta janela, ou nenhuma passou do limite para responder.
          Amplie o período; se continuar vazio, a fila de <strong>Lacunas</strong> mostra o que
          chegou e não encontrou resposta.
        </Vazio>
      ) : (
        <Table.ScrollContainer minWidth={700}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>FAQ</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Usos</Table.Th>
                <Table.Th>👍</Table.Th>
                <Table.Th>👎</Table.Th>
                <Table.Th>❓</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dados.faqs.map((f) => (
                <Table.Tr
                  key={f.faqId}
                  bg={f.negativos > 0 ? 'var(--mantine-color-red-light)' : undefined}
                >
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      {f.negativos > 0 && (
                        <Badge color="red" size="xs">
                          revisar
                        </Badge>
                      )}
                      <Text size="sm">{f.pergunta ?? '(FAQ removida da base)'}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{f.categoria ?? '?'}</Table.Td>
                  <Table.Td>{f.acionamentos}</Table.Td>
                  <Table.Td>{f.positivos}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c={f.negativos > 0 ? 'red' : undefined} fw={f.negativos > 0 ? 600 : undefined}>
                      {f.negativos}
                    </Text>
                  </Table.Td>
                  <Table.Td>{f.indefinidos}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Text size="xs" c="dimmed">
        Ordenado por 👎 primeiro: quem abre esta tela quer ver o que está errado, não o ranking de
        uso.
      </Text>
    </Stack>
  );
}

/**
 * Cartão de número. `Badge` "revisar" e o rótulo carregam o significado; a
 * cor não é o único sinal em lugar nenhum desta tela.
 */
function Cartao({
  rotulo,
  valor,
  nota,
}: {
  rotulo: string;
  valor: number | string;
  nota?: string;
}): JSX.Element {
  return (
    <Card withBorder padding="sm">
      <Text fz="xl" fw={600} lh={1.1}>
        {valor}
      </Text>
      <Text size="xs" c="dimmed">
        {rotulo}
      </Text>
      {nota && (
        <Text size="xs" c="dimmed">
          {nota}
        </Text>
      )}
    </Card>
  );
}
